import styled from 'styled-components';
import React, { FC, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { parse } from 'query-string';
import { isNil } from 'lodash-es';
import { CampaignStatus, CampaignType, CampaignTypeFilter } from '@dev/ee-api/ee-api-models';
import { Paper } from '@dev/ee-components';

import useMobile from '@/services/hooks/useMobile';
import dayjs from '@/services/tools/dayjs';
import { GetCampaigns, UnpickAllCampaigns } from '@/store/campaigns/actions';
import { LONG_DATE_FORMAT } from '@/services/constants';

import DraftsTable from './components/DraftTable';
import MobileDraftsTable from './components/MobileDraftTable';
import MobileFiltersComponent from './components/MobileFilters';
import CustomPagination from './components/Pagination';

const CampaignsWrapper = styled.div`
    margin-top: 32px;
    .MuiTableCell-root {
        font-weight: 400;
    }

    .MuiTypography-body1 a {
        white-space: nowrap;
    }

    .campaign-name {
        word-break: break-all;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .MuiTableSortLabel-root {
        white-space: nowrap;
    }
`;

const DraftsList: FC = (): JSX.Element => {
    const dispatch = useDispatch();
    const location = useLocation();
    const isMobile = useMobile();

    const query = parse(location.search, { parseNumbers: true });
    const { page, perPage, search, to, from, status, type } = query;

    const offset = !perPage || !page ? null : Number(page) * Number(perPage) - Number(perPage);

    const queryParams = {
        ...(isNil(perPage) ? {} : { limit: perPage }),
        ...(isNil(offset) ? {} : { offset }),
        ...(isNil(search) ? {} : { search }),
        ...(isNil(to)
            ? {}
            : {
                  to: dayjs(to.toString()).format(LONG_DATE_FORMAT),
              }),
        ...(isNil(from) ? {} : { from }),
        ...(isNil(type) ? {} :
            typeof status === 'string'
            ? { type: [type as CampaignType] }
            : { type: type as CampaignType[] }),
        ...(isNil(status)
            ? {}
            : typeof status === 'string'
            ? { status: [status as CampaignStatus] }
            : { status: status as CampaignStatus[] }),
    };

    const screenCampaignType = [CampaignTypeFilter.Draft];

    useEffect(() => {
        dispatch(GetCampaigns({ ...queryParams, status: screenCampaignType }));
        return () => {
            dispatch(UnpickAllCampaigns());
        };
    }, []);

    return (
        <CampaignsWrapper>
            {isMobile ? (
                <>
                    <MobileFiltersComponent />
                    <MobileDraftsTable />
                    <CustomPagination />
                </>
            ) : (
                <Paper>
                    <DraftsTable />
                    <CustomPagination />
                </Paper>
            )}
        </CampaignsWrapper>
    );
};

export default DraftsList;
